// screens/SettingsScreen/GeofenceSettings.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Slider,
  Paper,
  alpha,
  useTheme,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Save as SaveIcon,
  GpsFixed as GpsIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PhoneAndroid as PhoneAndroidIcon,
} from "@mui/icons-material";
import { getSystemSettings, updateSystemSettings } from "../../api/services/SystemSettings/settings";
import { useSnackbar } from "notistack";

const GeofenceSettings = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState({
    geofence_radius_meters: 100,
    geofence_enabled: true,
    gps_error_buffer_meters: 20,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getSystemSettings();
      setSettings(data);
    } catch (error) {
      enqueueSnackbar("Failed to load settings", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSystemSettings(settings);
      enqueueSnackbar("Settings updated successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to update settings", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const radiusMarks = [
    { value: 10, label: "10m" },
    { value: 50, label: "50m" },
    { value: 100, label: "100m" },
    { value: 200, label: "200m" },
    { value: 300, label: "300m" },
    { value: 400, label: "400m" },
    { value: 500, label: "500m" },
  ];

  const bufferMarks = [
    { value: 0, label: "0m" },
    { value: 20, label: "20m" },
    { value: 40, label: "40m" },
    { value: 60, label: "60m" },
    { value: 80, label: "80m" },
    { value: 100, label: "100m" },
    { value: 120, label: "120m" },
    { value: 130, label: "130m" },
  ];

  const totalRadius = settings.geofence_radius_meters + settings.gps_error_buffer_meters;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} color="#1A1A2E" sx={{ mb: 3 }}>
        Geofencing Configuration
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
            }}
          >
            <Stack spacing={3}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                    Enable Geofencing
                  </Typography>
                  <Typography variant="caption" color="#6B7280">
                    Enable or disable location verification for visits
                  </Typography>
                </Box>
                <Switch
                  checked={settings.geofence_enabled}
                  onChange={(e) => handleChange("geofence_enabled", e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#2469F0",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2469F0",
                    },
                  }}
                />
              </Box>

              <Divider />

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                      Base Verification Radius
                    </Typography>
                    <Typography variant="caption" color="#6B7280">
                      Maximum distance allowed from client location
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: "8px",
                      bgcolor: alpha("#2469F0", 0.08),
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} color="#2469F0">
                      {settings.geofence_radius_meters}m
                    </Typography>
                  </Paper>
                </Box>

                <Slider
                  value={settings.geofence_radius_meters}
                  onChange={(e, value) => handleChange("geofence_radius_meters", value)}
                  min={10}
                  max={500}
                  step={10}
                  marks={radiusMarks}
                  valueLabelDisplay="auto"
                  disabled={!settings.geofence_enabled}
                  sx={{
                    color: settings.geofence_enabled ? "#2469F0" : "#94A3B8",
                    "& .MuiSlider-thumb": {
                      bgcolor: settings.geofence_enabled ? "#2469F0" : "#94A3B8",
                    },
                    "& .MuiSlider-track": {
                      bgcolor: settings.geofence_enabled ? "#2469F0" : "#94A3B8",
                    },
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="caption" color="#6B7280">
                    Minimum: 10m
                  </Typography>
                  <Typography variant="caption" color="#6B7280">
                    Maximum: 500m
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <GpsIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                        GPS Error Buffer
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="#6B7280">
                      Extra distance to compensate for GPS inaccuracies (Max: 130m)
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: "8px",
                      bgcolor: alpha("#8B5CF6", 0.08),
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} color="#8B5CF6">
                      +{settings.gps_error_buffer_meters}m
                    </Typography>
                  </Paper>
                </Box>

                <Slider
                  value={settings.gps_error_buffer_meters}
                  onChange={(e, value) => handleChange("gps_error_buffer_meters", value)}
                  min={0}
                  max={130}
                  step={10}
                  marks={bufferMarks}
                  valueLabelDisplay="auto"
                  disabled={!settings.geofence_enabled}
                  sx={{
                    color: settings.geofence_enabled ? "#8B5CF6" : "#94A3B8",
                    "& .MuiSlider-thumb": {
                      bgcolor: settings.geofence_enabled ? "#8B5CF6" : "#94A3B8",
                    },
                    "& .MuiSlider-track": {
                      bgcolor: settings.geofence_enabled ? "#8B5CF6" : "#94A3B8",
                    },
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="caption" color="#6B7280">
                    No buffer
                  </Typography>
                  <Typography variant="caption" color="#6B7280">
                    Maximum: 130m buffer
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                      Total Effective Radius
                    </Typography>
                    <Typography variant="caption" color="#6B7280">
                      Base radius + GPS Error Buffer
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderRadius: "8px",
                      bgcolor: alpha("#22C55E", 0.1),
                      border: "2px solid #22C55E",
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} color="#22C55E">
                      {totalRadius}m
                    </Typography>
                  </Paper>
                </Box>
                <Typography variant="caption" color="#6B7280" sx={{ display: "block", mt: 1 }}>
                  Carers will be verified within {totalRadius}m ({settings.geofence_radius_meters}m base + {settings.gps_error_buffer_meters}m GPS error buffer)
                </Typography>
              </Box>

              {settings.geofence_enabled && (
                <Alert
                  severity="info"
                  sx={{ borderRadius: 2 }}
                  icon={<LocationIcon />}
                >
                  <Typography variant="body2">
                    Carers will be verified within{" "}
                    <strong>{totalRadius} meters</strong> of the client's location.
                    {settings.gps_error_buffer_meters > 0 && (
                      <span> GPS error buffer: <strong>+{settings.gps_error_buffer_meters}m</strong> extra tolerance.</span>
                    )}
                  </Typography>
                </Alert>
              )}

              {!settings.geofence_enabled && (
                <Alert
                  severity="warning"
                  sx={{ borderRadius: 2 }}
                  icon={<WarningIcon />}
                >
                  <Typography variant="body2">
                    Geofencing is currently <strong>disabled</strong>. Location
                    verification will be skipped.
                  </Typography>
                </Alert>
              )}

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    bgcolor: "#2469F0",
                    px: 4,
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                    },
                  }}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              bgcolor: "#F8FAFC",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E" sx={{ mb: 2 }}>
              Summary
            </Typography>

            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: settings.geofence_enabled ? "#D1FAE5" : "#FEE2E2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {settings.geofence_enabled ? (
                    <CheckCircleIcon sx={{ color: "#22C55E", fontSize: 18 }} />
                  ) : (
                    <WarningIcon sx={{ color: "#EF4444", fontSize: 18 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Status
                  </Typography>
                  <Typography variant="caption" color={settings.geofence_enabled ? "#22C55E" : "#EF4444"}>
                    {settings.geofence_enabled ? "Active" : "Disabled"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: "#EFF8FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GpsIcon sx={{ color: "#2469F0", fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Base Radius
                  </Typography>
                  <Typography variant="caption" color="#6B7280">
                    {settings.geofence_radius_meters}m
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: "#F3E8FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PhoneAndroidIcon sx={{ color: "#8B5CF6", fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    GPS Error Buffer
                  </Typography>
                  <Typography variant="caption" color="#6B7280">
                    +{settings.gps_error_buffer_meters}m
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  bgcolor: alpha("#22C55E", 0.05),
                  border: "1px solid #22C55E",
                }}
              >
                <Typography variant="caption" color="#22C55E" sx={{ display: "block", fontWeight: 600 }}>
                  Total Effective Radius
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#22C55E">
                  {totalRadius}m
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="#6B7280" sx={{ display: "block" }}>
                  GPS can have errors up to 10-25 meters. Adding a buffer helps prevent false negatives while maintaining security.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeofenceSettings;